import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Users, Loader2, Search, Download, RefreshCw, BellRing, Calendar as CalendarIcon,
  Layers, Wallet, TrendingUp, Activity, Coins, X, CheckCheck,
} from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePersistedState } from '@/hooks/usePersistedState';
import SegmentationImportWizard from './SegmentationImportWizard';

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const num = (v, d = 2) => Number(v || 0).toFixed(d);

const PRESETS = [
  { id: 'all', label: 'All Time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this-week', label: 'This Week' },
  { id: 'last-week', label: 'Last Week' },
  { id: 'this-month', label: 'This Month' },
  { id: 'last-month', label: 'Last Month' },
];

const resolveRange = (preset, customFrom, customTo) => {
  const today = new Date();
  const iso = (d) => format(d, 'yyyy-MM-dd');
  switch (preset) {
    case 'today': return { from: iso(today), to: iso(today) };
    case 'yesterday': { const y = subDays(today, 1); return { from: iso(y), to: iso(y) }; }
    case 'this-week': return { from: iso(startOfWeek(today, { weekStartsOn: 1 })), to: iso(endOfWeek(today, { weekStartsOn: 1 })) };
    case 'last-week': { const w = subWeeks(today, 1); return { from: iso(startOfWeek(w, { weekStartsOn: 1 })), to: iso(endOfWeek(w, { weekStartsOn: 1 })) }; }
    case 'this-month': return { from: iso(startOfMonth(today)), to: iso(endOfMonth(today)) };
    case 'last-month': { const m = subMonths(today, 1); return { from: iso(startOfMonth(m)), to: iso(endOfMonth(m)) }; }
    case 'custom': return { from: customFrom || null, to: customTo || null };
    default: return { from: null, to: null };
  }
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="relative p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden group">
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--text-muted))]">{label}</p>
          <p className="text-lg font-bold text-white font-mono mt-1">{value}</p>
        </div>
        <Icon size={15} style={{ color: accent }} className="opacity-70 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}

function SegmentCard({ meta, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`seg-card-${meta.key}`}
      className={`relative text-left p-3 pl-4 rounded-xl border overflow-hidden transition-all duration-200 ${
        active
          ? 'border-white/40 bg-white/[0.06] shadow-lg -translate-y-0.5'
          : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:border-white/25 hover:-translate-y-0.5'
      }`}
    >
      <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: meta.color }} />
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] leading-tight" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <span className="text-xl font-bold text-white font-mono leading-none">{count}</span>
      </div>
      <p className="text-[10px] text-[hsl(var(--text-muted))] leading-snug">{meta.action}</p>
    </button>
  );
}

export default function SegmentationIntelligence({ ib }) {
  const ibId = ib?.id;
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const P = `seg-intel:${ibId || 'none'}`;
  const [brokerFilter, setBrokerFilter] = usePersistedState(`${P}:broker`, 'all');
  const [segFilter, setSegFilter] = usePersistedState(`${P}:segment`, 'all');
  const [search, setSearch] = usePersistedState(`${P}:search`, '');
  const [sortKey, setSortKey] = usePersistedState(`${P}:sort`, 'total_lots');
  const [preset, setPreset] = usePersistedState(`${P}:preset`, 'all');
  const [customFrom, setCustomFrom] = usePersistedState(`${P}:customFrom`, '');
  const [customTo, setCustomTo] = usePersistedState(`${P}:customTo`, '');
  const [showAlerts, setShowAlerts] = useState(false);

  const range = useMemo(() => resolveRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const load = () => {
    if (!ibId) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = {};
    if (brokerFilter !== 'all') params.broker = brokerFilter;
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    axios.get(`${API}/api/segmentation/${ibId}/intelligence`, { params, headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    axios.get(`${API}/api/segmentation/${ibId}/alerts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAlerts(r.data))
      .catch(() => setAlerts({ alerts: [], unread: 0 }));
  };

  useEffect(load, [ibId, brokerFilter, range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAlertsRead = async () => {
    const token = localStorage.getItem('token');
    await axios.post(`${API}/api/segmentation/${ibId}/alerts/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Alert ditandakan dibaca');
    load();
  };

  const openDetail = async (acct) => {
    const token = localStorage.getItem('token');
    try {
      const r = await axios.get(`${API}/api/segmentation/${ibId}/account/${acct.broker}/${acct.account_no}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDetail(r.data.account);
    } catch {
      toast.error('Gagal buka detail account');
    }
  };

  const segMeta = data?.segments || [];
  const metaByKey = useMemo(() => Object.fromEntries(segMeta.map((m) => [m.key, m])), [segMeta]);

  const rows = useMemo(() => {
    let out = data?.accounts || [];
    if (segFilter !== 'all') out = out.filter((a) => a.segments.includes(segFilter));
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((a) => `${a.client_name} ${a.account_no} ${a.wallet_id}`.toLowerCase().includes(q));
    return [...out].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [data, segFilter, search, sortKey]);

  const exportCsv = () => {
    if (!rows.length) return;
    const cols = ['broker', 'client_name', 'account_no', 'wallet_id', 'total_lots', 'total_commission',
      'balance', 'equity', 'pnl', 'pnl_pct', 'total_trades', 'trading_days', 'last_trade',
      'days_since_trade', 'segments'];
    const csv = [cols.join(','),
      ...rows.map((r) => cols.map((c) => `"${Array.isArray(r[c]) ? r[c].join('|') : (r[c] ?? '')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `segmentation_${ib?.name || ibId}_${segFilter}.csv`;
    a.click();
  };

  const totals = data?.totals || {};
  const rangeLabel = preset === 'custom'
    ? (customFrom || customTo ? `${customFrom || '…'} → ${customTo || '…'}` : 'Custom Range')
    : (PRESETS.find((p) => p.id === preset)?.label || 'All Time');

  return (
    <div className="space-y-5" data-testid="seg-intelligence">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E63946]/20 via-[#7C3AED]/10 to-transparent border-b border-[hsl(var(--border))]">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#7C3AED]/20 border border-[#E63946]/40">
              <Layers size={19} className="text-white" />
            </div>
            <div className="mr-auto">
              <h3 className="text-lg font-bold text-white font-heading leading-tight">Client Intelligence</h3>
              <p className="text-xs text-[hsl(var(--text-muted))]">
                Data broker · lot & komisen ikut range dipilih, balance & equity ikut snapshot terkini
              </p>
            </div>

            {alerts.unread > 0 && (
              <button onClick={() => setShowAlerts(true)} data-testid="seg-alert-bell"
                className="h-9 px-3 rounded-md border border-[#f59e0b]/50 bg-[#f59e0b]/10 text-xs text-[#f59e0b] font-semibold flex items-center gap-1.5 hover:bg-[#f59e0b]/20">
                <BellRing size={13} /> {alerts.unread} alert baru
              </button>
            )}

            {/* Date range */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="h-9 px-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-xs text-white flex items-center gap-1.5 hover:border-[#E63946]"
                  data-testid="seg-date-trigger">
                  <CalendarIcon size={13} /> {rangeLabel}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4 bg-[hsl(var(--surface))] border-[hsl(var(--border))]" data-testid="seg-date-popover">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[hsl(var(--text-muted))] mb-2">Quick Select</p>
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {PRESETS.map((p) => (
                    <button key={p.id} onClick={() => setPreset(p.id)} data-testid={`seg-date-${p.id}`}
                      className={`px-2 py-1.5 rounded-md text-[11px] text-left transition-colors ${
                        preset === p.id ? 'bg-[#E63946]/20 text-[#E63946] font-semibold' : 'text-white hover:bg-[hsl(var(--surface-elevated))]'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[hsl(var(--text-muted))] mb-2">Custom</p>
                <div className="flex items-center gap-1.5">
                  <input type="date" value={customFrom} data-testid="seg-date-from"
                    onChange={(e) => { setCustomFrom(e.target.value); setPreset('custom'); }}
                    className="flex-1 h-8 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] px-2 text-[11px] text-white" />
                  <span className="text-[10px] text-[hsl(var(--text-muted))]">→</span>
                  <input type="date" value={customTo} data-testid="seg-date-to"
                    onChange={(e) => { setCustomTo(e.target.value); setPreset('custom'); }}
                    className="flex-1 h-8 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] px-2 text-[11px] text-white" />
                </div>
              </PopoverContent>
            </Popover>

            <button onClick={() => setImportOpen(true)}
              className="h-9 px-4 rounded-md bg-[#E63946] text-xs font-semibold text-white flex items-center gap-1.5 hover:bg-[#c92f3c] transition-colors"
              data-testid="seg-import-open">
              <Upload size={13} /> Import Report Broker
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar border-b border-[hsl(var(--border))]">
          <Select value={brokerFilter} onValueChange={setBrokerFilter}>
            <SelectTrigger className="w-[140px] shrink-0 h-8 text-[11px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="seg-broker-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Broker</SelectItem>
              {(data?.brokers || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / account…"
              className="w-full h-8 pl-9 pr-3 rounded-md bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-[11px] text-white outline-none focus:border-[#E63946] transition-colors"
              data-testid="seg-search" />
          </div>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger className="w-[160px] shrink-0 h-8 text-[11px] bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]" data-testid="seg-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="total_lots">Sort: Traded Lot</SelectItem>
              <SelectItem value="total_commission">Sort: Commission</SelectItem>
              <SelectItem value="equity">Sort: Equity</SelectItem>
              <SelectItem value="pnl_pct">Sort: P&L %</SelectItem>
              <SelectItem value="days_since_trade">Sort: Hari Tak Trade</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} className="h-8 px-3 shrink-0 rounded-md border border-[hsl(var(--border))] text-[11px] text-white flex items-center gap-1.5 hover:border-white/40" data-testid="seg-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={exportCsv} className="h-8 px-3 shrink-0 rounded-md border border-[hsl(var(--border))] text-[11px] text-white flex items-center gap-1.5 hover:border-white/40" data-testid="seg-export">
            <Download size={12} /> Export
          </button>
          {data?.brokers?.length ? (
            <div className="ml-auto flex items-center gap-1.5 shrink-0 text-[10px] text-[hsl(var(--text-muted))]" data-testid="seg-last-import">
              <span className="uppercase tracking-[0.1em] font-bold">Import terakhir</span>
              {data.brokers.map((b) => (
                <span key={b} className="px-2 py-0.5 rounded border border-[hsl(var(--border))] font-mono">
                  {b} {data.last_import?.[b] || '—'}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* Stats */}
        <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="seg-totals">
          <StatCard icon={Users} label="Accounts" value={(totals.accounts || 0).toLocaleString()} accent="#7C3AED" />
          <StatCard icon={Activity} label="Yang Trade" value={(totals.traded_accounts || 0).toLocaleString()} accent="#10b981" />
          <StatCard icon={Layers} label="Total Lots" value={num(totals.total_lots)} accent="#22d3ee" />
          <StatCard icon={Coins} label="Commission" value={money(totals.total_commission)} accent="#E63946" />
          <StatCard icon={Wallet} label="Total Balance" value={money(totals.total_balance)} accent="#f59e0b" />
          <StatCard icon={TrendingUp} label="Total Equity" value={money(totals.total_equity)} accent="#ec4899" />
        </div>
      </div>

      {/* ── Segments ───────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase font-bold tracking-[0.15em] text-[hsl(var(--text-muted))]">
            Segment & Pendekatan Sales
          </p>
          {segFilter !== 'all' && (
            <button onClick={() => setSegFilter('all')} className="text-[10px] text-[#E63946] flex items-center gap-1 hover:underline" data-testid="seg-clear-filter">
              <X size={10} /> Clear filter
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {segMeta.map((m) => (
            <SegmentCard key={m.key} meta={m} count={data?.counts?.[m.key] || 0}
              active={segFilter === m.key}
              onClick={() => setSegFilter(segFilter === m.key ? 'all' : m.key)} />
          ))}
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────── */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden">
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center gap-2">
          <Users size={14} className="text-[hsl(var(--text-muted))]" />
          <p className="text-xs font-semibold text-white">
            {rows.length.toLocaleString()} account{segFilter !== 'all' ? ` · ${metaByKey[segFilter]?.label}` : ''}
          </p>
          <span className="text-[10px] text-[hsl(var(--text-muted))] ml-auto font-mono">{rangeLabel}</span>
        </div>
        {loading ? (
          <div className="h-40 flex items-center justify-center text-[hsl(var(--text-muted))]">
            <Loader2 size={18} className="animate-spin mr-2" /> Memuatkan…
          </div>
        ) : !rows.length ? (
          <div className="p-10 text-center" data-testid="seg-empty">
            <Upload size={22} className="mx-auto mb-2 text-[hsl(var(--text-muted))]" />
            <p className="text-sm text-white mb-1">Tiada account untuk paparan ni</p>
            <p className="text-xs text-[hsl(var(--text-muted))]">
              Klik <span className="text-white font-semibold">Import Report Broker</span> untuk upload report, atau luaskan range tarikh.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[640px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[hsl(var(--surface-elevated))] z-10">
                <tr>
                  {['Client', 'Broker', 'Account', 'Lots', 'Commission', 'Balance', 'Equity', 'P&L', 'Trades', 'Trade Days', 'Last Trade', 'Segment'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--text-muted))] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 400).map((a) => (
                  <tr key={`${a.broker}-${a.account_no}`} onClick={() => openDetail(a)}
                    className="border-t border-[hsl(var(--border))]/50 hover:bg-white/[0.04] cursor-pointer transition-colors"
                    data-testid={`seg-row-${a.account_no}`}>
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap max-w-[180px] truncate">{a.client_name}</td>
                    <td className="px-3 py-2 text-[hsl(var(--text-muted))]">{a.broker}</td>
                    <td className="px-3 py-2 font-mono text-[hsl(var(--text-muted))]">{a.account_no}</td>
                    <td className="px-3 py-2 font-mono text-white">{num(a.total_lots)}</td>
                    <td className="px-3 py-2 font-mono text-white">{money(a.total_commission)}</td>
                    <td className="px-3 py-2 font-mono text-[hsl(var(--text-muted))]">{money(a.balance)}</td>
                    <td className="px-3 py-2 font-mono text-white">{money(a.equity)}</td>
                    <td className={`px-3 py-2 font-mono ${a.pnl_pct >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                      {a.pnl_pct >= 0 ? '+' : ''}{num(a.pnl_pct, 1)}%{a.pnl_estimated ? '*' : ''}
                    </td>
                    <td className="px-3 py-2 font-mono text-[hsl(var(--text-muted))]">{a.total_trades}</td>
                    <td className="px-3 py-2 font-mono text-[hsl(var(--text-muted))]">{a.trading_days}/{a.window_days}</td>
                    <td className="px-3 py-2 text-[hsl(var(--text-muted))] whitespace-nowrap">
                      {a.last_trade || '—'}{a.days_since_trade != null ? ` (${a.days_since_trade}h)` : ''}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {a.segments.map((s) => (
                          <span key={s} className="px-1.5 py-0.5 rounded text-[9px] font-semibold border"
                            style={{ color: metaByKey[s]?.color, borderColor: `${metaByKey[s]?.color}55` }}>
                            {metaByKey[s]?.label || s}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-4 py-2 text-[10px] text-[hsl(var(--text-muted))] border-t border-[hsl(var(--border))]">
          * P&L anggaran — broker tak sediakan deposit/withdrawal, jadi dikira dari Equity vs Balance/baseline.
        </p>
      </div>

      <SegmentationImportWizard open={importOpen} onOpenChange={setImportOpen} ibId={ibId} onImported={load} />

      {/* ── Alerts modal ───────────────────────────────────── */}
      <Dialog open={showAlerts} onOpenChange={setShowAlerts}>
        <DialogContent className="max-w-2xl bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="seg-alerts-modal">
          <DialogTitle className="sr-only">Segment Alerts</DialogTitle>
        <DialogDescription className="sr-only">Segment Alerts</DialogDescription>
          <div className="flex items-center gap-2 mb-3">
            <BellRing size={16} className="text-[#f59e0b]" />
            <h3 className="text-base font-bold text-white">Alert Segment ({alerts.unread} belum dibaca)</h3>
            <button onClick={markAlertsRead} className="ml-auto text-[11px] text-[#10b981] flex items-center gap-1 hover:underline" data-testid="seg-alerts-read">
              <CheckCheck size={12} /> Tandakan semua dibaca
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {!alerts.alerts.length ? (
              <p className="text-xs text-[hsl(var(--text-muted))] py-6 text-center">Tiada alert.</p>
            ) : alerts.alerts.map((al, i) => (
              <div key={`${al.account_no}-${al.segment}-${i}`}
                className={`p-3 rounded-lg border flex items-start gap-3 ${al.read ? 'border-[hsl(var(--border))] opacity-60' : 'border-[#f59e0b]/40 bg-[#f59e0b]/5'}`}>
                <span className="mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold border shrink-0"
                  style={{ color: metaByKey[al.segment]?.color, borderColor: `${metaByKey[al.segment]?.color}55` }}>
                  {metaByKey[al.segment]?.label || al.segment}
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-white font-medium truncate">{al.client_name} · {al.broker} {al.account_no}</p>
                  <p className="text-[11px] text-[hsl(var(--text-muted))]">
                    {al.reason} · equity {money(al.equity)} · {num(al.lots)} lot
                    {al.last_trade ? ` · last trade ${al.last_trade}` : ''}
                  </p>
                  <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">{metaByKey[al.segment]?.action}</p>
                </div>
                <span className="ml-auto text-[10px] text-[hsl(var(--text-muted))] font-mono shrink-0">{al.date}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Account detail ─────────────────────────────────── */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="seg-detail-modal">
          <DialogTitle className="sr-only">Account Detail</DialogTitle>
        <DialogDescription className="sr-only">Account Detail</DialogDescription>
          {detail && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white">{detail.client_name}</h3>
                <p className="text-xs text-[hsl(var(--text-muted))] font-mono">
                  {detail.broker} · {detail.account_no} · {detail.account_type || '—'} · {detail.country || '—'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                {detail.segments.map((s) => (
                  <span key={s} className="px-2 py-0.5 rounded text-[10px] font-semibold border"
                    style={{ color: metaByKey[s]?.color, borderColor: `${metaByKey[s]?.color}55` }}>
                    {metaByKey[s]?.label || s}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { l: 'Total Lots', v: num(detail.total_lots) },
                  { l: 'Commission', v: money(detail.total_commission) },
                  { l: 'Balance', v: money(detail.balance) },
                  { l: 'Equity', v: money(detail.equity) },
                  { l: 'P&L', v: `${detail.pnl >= 0 ? '+' : ''}${money(detail.pnl)} (${num(detail.pnl_pct, 1)}%)` },
                  { l: 'Trades', v: detail.total_trades },
                  { l: 'Trading Days', v: `${detail.trading_days}/${detail.window_days}` },
                  { l: 'Last Trade', v: `${detail.last_trade || '—'}${detail.days_since_trade != null ? ` (${detail.days_since_trade}h)` : ''}` },
                ].map((x) => (
                  <div key={x.l} className="p-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                    <p className="text-[9px] uppercase tracking-[0.1em] text-[hsl(var(--text-muted))]">{x.l}</p>
                    <p className="text-xs font-mono font-semibold text-white">{x.v}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-[0.15em] text-[hsl(var(--text-muted))] mb-1">Lot harian (60 hari terakhir)</p>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={detail.series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'hsl(var(--text-muted))' }} />
                      <YAxis tick={{ fontSize: 9, fill: 'hsl(var(--text-muted))' }} width={40} />
                      <Tooltip contentStyle={{ background: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', fontSize: 11 }} />
                      <Bar dataKey="lots" fill="#10b981" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="p-3 rounded-lg border border-[#E63946]/30 bg-[#E63946]/5">
                <p className="text-[10px] uppercase font-bold tracking-[0.15em] text-[hsl(var(--text-muted))] mb-1">Cadangan pendekatan</p>
                <ul className="space-y-1">
                  {detail.segments.map((s) => metaByKey[s] && (
                    <li key={s} className="text-xs text-white/90">• {metaByKey[s].action}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
